import React from 'react';
import { createGlobalStyle } from 'styled-components';
import * as database from './db.js';

function getUrl(url, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';

    xhr.addEventListener(
      'load',
      () => {
        if (xhr.status === 200) {
          resolve(xhr.response);
        } else {
          reject(new Error('Status is not 200.'));
        }
      },
      false
    );

    xhr.addEventListener('error', e => reject(e));
    xhr.addEventListener('abort', e => reject(e));
    xhr.addEventListener('progress', e => {
      if (e.lengthComputable) {
        onProgress(e.loaded, e.total);
      }
    });

    try {
      xhr.send();
    } catch (e) {
      reject(e);
    }
  });
}

export default class Preloader {
  constructor(urls = [], fonts = []) {
    this.urls = urls;
    this.fonts = fonts;
    this.objectUrls = new Map();
    this.load(urls, fonts);
    this.fontStyle = null;
  }

  async load(urls, fonts) {
    this.totalBytes = 0;
    this.knownTotalSize = false;
    const allUrls = urls
      .concat(fonts.map(f => f.url))
      .filter(url => !url.startsWith('data:'));
    // TODO this is not needed if everything is cached!
    this.sizes = await this.getSizes(allUrls);
    this.knownTotalSize = true;
    await this.loadUrls(this.sizes);
  }

  async getSizes(urls) {
    let sizes = [];
    for (let i = 0; i < urls.length; i++) {
      try {
        const response = await fetch(urls[i], { method: 'HEAD' });
        const bytes = parseInt(response.headers.get('Content-Length'), 10);
        sizes.push({ url: urls[i], total: bytes, loaded: 0 });
      } catch (e) {
        // Some environments don't support HEAD to get content length.
        sizes.push({ url: urls[i], total: 1, loaded: 0 });
      }
    }
    return sizes;
  }

  async loadUrls(sizes) {
    let db;
    try {
      db = await database.openDatabase();
      console.log(db);
    } catch (e) {
      console.error(e);
    }

    for (let i = 0; i < sizes.length; i++) {
      if (db) {
        try {
          const url = await database.fetch(db, sizes[i].url);
          if (url) {
            console.log('HIT', url);
            sizes[i].loaded = sizes[i].total;
            this.objectUrls.set(sizes[i].url, url);
            continue;
          }
        } catch (e) {
          console.error(e);
        }
      }

      try {
        const blob = await getUrl(sizes[i].url, (loadedBytes, totalBytes) => {
          // normalize to prefetch total bytes
          sizes[i].loaded = loadedBytes * (sizes[i].total / totalBytes);
        });
        this.objectUrls.set(sizes[i].url, URL.createObjectURL(blob));

        try {
          await database.store(db, sizes[i].url, blob);
        } catch (e) {
          console.error(e);
        }
      } catch (e) {
        console.error(e);
      } finally {
        sizes[i].loaded = sizes[i].total;
      }
    }

    try {
      await database.clear(db, sizes.map(s => s.url));
    } catch (e) {
      console.error(e);
    }
  }

  get = url => {
    if (this.objectUrls.has(url)) {
      return this.objectUrls.get(url);
    } else {
      return url;
    }
  };

  getFontStyle = () => {
    if (this.fontStyle == null && this.getProgress() >= 1) {
      const FontStyle = createGlobalStyle`
        ${this.fonts
          .map(
            ({ name, url }) => `
          @font-face {
            font-family: '${name}';
            src: url('${this.get(url)}') format('woff');
            font-weight: normal;
            font-style: normal;
          }
        `
          )
          .join('\n')}
      `;
      this.fontStyle = <FontStyle />;
    }
    // return null if fonts are not loaded yet
    return this.fontStyle;
  };

  getProgress = () => {
    if (!this.knownTotalSize) return 0;
    const total = this.sizes.reduce((total, s) => s.total + total, 0);
    if (total === 0) return 1;
    const loaded = this.sizes.reduce((total, s) => s.loaded + total, 0);
    return loaded / total;
  };
}
